import type {
  BleTransport,
  ConnectionState,
  DeviceConfig,
  TransportEvents,
} from './types';
import { DEFAULT_DEVICE_CONFIG, LEGACY_V41_CONFIG } from './types';

/**
 * Web Bluetooth transport. Works on Android Chrome, desktop Chrome/Edge, and
 * the Bluefy browser on iOS. Does NOT work in iOS Safari — see types.ts.
 */
export class WebBluetoothTransport implements BleTransport {
  readonly id = 'web-bluetooth' as const;
  readonly label = 'Web Bluetooth';

  state: ConnectionState = 'disconnected';

  private device: BluetoothDevice | null = null;
  private rx: BluetoothRemoteGATTCharacteristic | null = null;
  private tx: BluetoothRemoteGATTCharacteristic | null = null;
  private decoder = new TextDecoder();
  private encoder = new TextEncoder();
  private reconnectAttempts = 0;
  private intentionalDisconnect = false;

  constructor(
    private config: DeviceConfig,
    private events: TransportEvents,
  ) {
    if (!WebBluetoothTransport.isSupported()) this.state = 'unsupported';
  }

  static isSupported(): boolean {
    return typeof navigator !== 'undefined' && navigator.bluetooth !== undefined;
  }

  get available(): boolean {
    return WebBluetoothTransport.isSupported();
  }

  private setState(state: ConnectionState) {
    this.state = state;
    this.events.onState(state);
  }

  private log(dir: 'tx' | 'rx' | 'info' | 'error', text: string) {
    this.events.onLog({ at: Date.now(), dir, text });
  }

  async connect(): Promise<void> {
    if (!this.available) {
      this.setState('unsupported');
      throw new Error('Web Bluetooth is not available in this browser.');
    }

    this.intentionalDisconnect = false;

    try {
      this.setState('scanning');
      // requestDevice must be called from a user gesture. Filtering by service
      // is more reliable than by name — as of v4.2 the firmware advertises the
      // service UUID (before that it advertised the name only).
      //
      // Both firmware generations are offered so the chooser lists the device
      // whether or not it has been reflashed; openGatt() works out which it is.
      const services = this.candidates().map((c) => c.serviceUuid);
      this.device = await navigator.bluetooth!.requestDevice({
        filters: [
          ...services.map((uuid) => ({ services: [uuid] })),
          { namePrefix: this.config.namePrefix },
        ],
        optionalServices: services,
      });

      this.log('info', `Selected ${this.device.name ?? 'device'}`);
      this.device.addEventListener('gattserverdisconnected', this.handleDisconnect);

      await this.openGatt();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      // A user closing the chooser is not an error worth shouting about.
      if (/cancel|user/i.test(message)) {
        this.log('info', 'Device chooser dismissed.');
      } else {
        this.log('error', message);
      }
      this.setState('disconnected');
      throw err;
    }
  }

  /**
   * The configured identifiers first, then the other firmware generation.
   *
   * v4.2 changed the UUIDs to canonical Nordic UART. Rather than make the user
   * find a settings toggle after reflashing — or worse, migrate their stored
   * choice and break the device they have NOT reflashed — the transport simply
   * tries both and keeps whichever answers.
   */
  private candidates(): DeviceConfig[] {
    const rest = [DEFAULT_DEVICE_CONFIG, LEGACY_V41_CONFIG].filter(
      (c) => c.serviceUuid !== this.config.serviceUuid,
    );
    return [this.config, ...rest];
  }

  private async openGatt(): Promise<void> {
    if (!this.device?.gatt) throw new Error('No GATT server on device.');

    this.setState('connecting');
    const server = await this.device.gatt.connect();

    this.setState('discovering');
    let lastError: unknown = null;
    for (const cfg of this.candidates()) {
      try {
        const service = await server.getPrimaryService(cfg.serviceUuid);
        this.rx = await service.getCharacteristic(cfg.rxUuid);
        this.tx = await service.getCharacteristic(cfg.txUuid);
        if (cfg.serviceUuid !== this.config.serviceUuid) {
          this.config = cfg;
          this.log(
            'info',
            cfg === LEGACY_V41_CONFIG
              ? 'Device is on firmware 4.1 or older — using its older identifiers.'
              : 'Device is on firmware 4.2 or newer — using the standard identifiers.',
          );
        }
        break;
      } catch (err) {
        lastError = err;
        this.rx = null;
        this.tx = null;
      }
    }
    if (!this.rx || !this.tx) {
      throw lastError instanceof Error
        ? lastError
        : new Error('The device did not offer the expected UART service.');
    }

    this.tx.addEventListener('characteristicvaluechanged', this.handleNotification);
    await this.tx.startNotifications();

    this.reconnectAttempts = 0;
    this.setState('connected');
    this.log('info', 'Connected. Subscribed to notifications.');
  }

  private handleNotification = (event: Event) => {
    const target = event.target as BluetoothRemoteGATTCharacteristic;
    if (!target.value) return;
    const text = this.decoder.decode(target.value);
    this.log('rx', text);
    // Each notification is one message. The firmware calls setValue()+notify()
    // per line with a 20ms gap, so we do not reassemble across notifications.
    // If MTU has not been negotiated above 23, long messages arrive TRUNCATED
    // rather than split — the parser degrades to 'unknown' rather than failing.
    this.events.onMessage(text);
  };

  private handleDisconnect = () => {
    this.rx = null;
    this.tx = null;
    if (this.intentionalDisconnect) {
      this.setState('disconnected');
      this.log('info', 'Disconnected.');
      return;
    }
    this.log('error', 'Connection lost.');
    void this.attemptReconnect();
  };

  /** Spec §7.2: up to 3 attempts, 2 second delay. */
  private async attemptReconnect(): Promise<void> {
    if (this.reconnectAttempts >= 3 || !this.device) {
      this.setState('disconnected');
      this.log('error', 'Reconnect failed. The device keeps running its program regardless.');
      return;
    }
    this.reconnectAttempts += 1;
    this.setState('reconnecting');
    this.log('info', `Reconnect attempt ${this.reconnectAttempts} of 3…`);
    await new Promise((r) => setTimeout(r, 2000));
    try {
      await this.openGatt();
    } catch {
      await this.attemptReconnect();
    }
  }

  async disconnect(): Promise<void> {
    this.intentionalDisconnect = true;
    try {
      await this.tx?.stopNotifications();
    } catch {
      // Already gone — nothing to do.
    }
    this.device?.gatt?.disconnect();
    this.rx = null;
    this.tx = null;
    this.setState('disconnected');
  }

  async send(command: string): Promise<void> {
    if (!this.rx) throw new Error('Not connected.');
    const data = this.encoder.encode(command);
    this.log('tx', command);
    // Prefer write-without-response: the RX characteristic supports both, and
    // it avoids a round trip per command across a 30+ command sequence.
    if (this.rx.writeValueWithoutResponse) {
      await this.rx.writeValueWithoutResponse(data);
    } else {
      await this.rx.writeValue(data);
    }
  }
}
